import React, {Key, useEffect, useState} from 'react';
import {wdiCore, WdiModalAppProps, WdiModalAppResult, WdiModalProps} from '@/components/WdiControls';
import {Button, Input, message, Popover, TableColumnsType} from 'antd';
import {WdiModal} from '@/components/WdiControls/WdiModalForm';
import {ResponseStatus} from '@/utils/request';
import {WdiTable} from '@/components/WdiControls/WdiTable';
import {CloseOutlined} from '@ant-design/icons';
import {Enum} from "@/enums/system";
import {ReversePrepayDetail} from "@/services/sales/sp";
import {Company} from "@/services/mdm/company";
import {SoldTo} from "@/services/mdm/soldto";
import {Supplier} from "@/services/mdm/supplier";
import {Staff} from "@/services/basic/staff";


type SpFilterParamProps = {
  paymentMethodId?: number,
  companyCode?: string,
  payToUserId?: number,
  supplierId?: number,
  soldToCode?: string,
  formSpDetail?: API.FormSpDetail;
}

export type ProjectSelectProps = {
  value?: API.ReversePrepayDetailVo[];
  onChange?: (value?: API.ReversePrepayDetailVo[]) => void;
  label?: string;
  filterParams?: SpFilterParamProps
};

export const SpPreSelectApp: React.FC<ProjectSelectProps> = (props) => {
  const [modalProps, setModalProps] = useState<WdiModalProps>({visible: false});
  const onClose = () => {
    setModalProps({visible: false, title: ""});
  };
  const onSave = (result?: WdiModalAppResult) => {
    setModalProps({visible: false, title: ""});
    if (result && result.code === ResponseStatus.SUCCESS) {
      props.onChange?.(result.data);
    }
  };

  const newProps: any = {
    onClick: () => {
      setModalProps({
        visible: true,
        title: "选择要冲销的预付单",
        data: {
          records: props.value,
          filterParams: props.filterParams
        },
      });
    },
    allowClear: {
      clearIcon: (
        <Button
          shape='circle'
          style={{border: "none", width: 20, height: 20}}
          icon={<CloseOutlined/>}
          size="small"
          onClick={() => props.onChange?.([])}
        />
      )
    }
  };

  return (
    <>
      <Popover
        content={props.value?.map(item => wdiCore.String.CodeOrName(item?.requisitionNumber, item?.transactionNum)).join()}>
        <Input
          {...newProps}
          placeholder={"选择要冲销的预付单"}
          value={props.value?.map(item => wdiCore.String.CodeOrName(item?.requisitionNumber, item?.transactionNum)).join()}
        />
      </Popover>
      {
        modalProps.visible &&
        <FormSpSelectModalApp
          {...modalProps}
          onClose={onClose}
          onSave={onSave}
        />
      }
    </>
  );
}

const FormSpSelectModalApp: React.FC<WdiModalAppProps> = (props) => {
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<API.ReversePrepayDetailVo[]>([]);
  const [selectedRowsState, setSelectedRowsState] = useState<{
    version: number,
    rows: Map<Key, API.ReversePrepayDetailVo>
  }>({version: 0, rows: new Map<Key, API.ReversePrepayDetailVo>()});
  const [tableColumns, setTableColumns] = useState<any[]>([]);


  useEffect(() => {
    (async () => {
      setTableColumns(await initTableColumns())
      fetchData();
    })()
  }, []);

  useEffect(() => {
    const records: API.ReversePrepayDetailVo[] = props.data?.records ?? [];
    records.forEach((item) => {
      addSelectedRowState(item);
    })
  }, [props.data?.records])

  const buildRowKey = (row: API.ReversePrepayDetailVo) => `${row.requisitionNumber}_${row.transactionNum}`;

  const addSelectedRowState = (row: API.ReversePrepayDetailVo) => {
    if (!row.requisitionNumber || !row.transactionNum) return;
    const rowMap = selectedRowsState.rows;
    rowMap.set(buildRowKey(row), row);
    setSelectedRowsState({version: selectedRowsState.version + 1, rows: rowMap})
  }

  const delSelectedRowState = (row: API.ReversePrepayDetailVo) => {
    if (!row.requisitionNumber || !row.transactionNum) return;
    const rowMap = selectedRowsState.rows;
    rowMap.delete(buildRowKey(row));
    setSelectedRowsState({version: selectedRowsState.version + 1, rows: rowMap})
  }
  const initTableColumns = async () => {
    const columns: TableColumnsType<API.ReversePrepayDetailVo> = [
      {
        title: 'TPM预付单号',
        dataIndex: 'tpmDocNum',
        ellipsis: true,
        width: 150,
      },
      {
        title: 'HEC预付单号',
        width: 150,
        dataIndex: 'requisitionNumber',
        ellipsis: true,
      },
      {
        title: 'HEC现金事务号',
        width: 150,
        dataIndex: 'transactionNum',
        ellipsis: true,
      },
      {
        title: '借款说明',
        width: 150,
        dataIndex: 'lineDesc',
        ellipsis: true,
      },
      {
        title: '申请人',
        width: 100,
        dataIndex: 'employeeName',
        ellipsis: true,
      },
      {
        title: '申请日期',
        width: 100,
        dataIndex: 'requisitionDate',
        ellipsis: true,
      },
      {
        title: '付款日期',
        width: 100,
        dataIndex: 'transactionDate',
        ellipsis: true,
      },
      {
        title: '预付金额',
        width: 105,
        dataIndex: 'amount',
        ellipsis: true,
        render: (value) => (
          <span>{wdiCore.Number.AmountToString(value)}</span>
        ),
      },
      {
        title: '当前余额',
        width: 110,
        dataIndex: 'writeOffAmount',
        ellipsis: true,
        render: (value) => (
          <span>{wdiCore.Number.AmountToString(value)}</span>
        ),
      },
    ];
    return columns
  }

  const getFilterOptions = (async () => {
    const filterParams: SpFilterParamProps = props.data?.filterParams;
    const filterOptions: any[] = [];
    //formsp.company_code如不为空则company其对应的salesOrgCode，如为空则取第一条明细的soldto的company的salesOrgCode
    if (filterParams?.companyCode) {
      const company = await Company.get(filterParams?.companyCode);
      filterOptions.push({
        title: 'salesOrgCode',
        field: 'salesOrgCode',
        opt: 7,
        value: company?.data?.salesOrgCode
      })
    } else {
      if (filterParams?.formSpDetail) {
        if (filterParams?.formSpDetail?.soldToCode) {
          const soldTo = await SoldTo.get(filterParams?.formSpDetail?.soldToCode);
          const company = await Company.get(soldTo?.data?.companyCode as string);
          filterOptions.push({
            title: 'salesOrgCode',
            field: 'salesOrgCode',
            opt: 7,
            value: company?.data?.salesOrgCode
          })
        }
      }
    }
    //支付方式是第三方&给供应商时为 VENDER，第三方&给员工时为EMPLOYEE，转账时为CUSTOMER
    if (filterParams?.paymentMethodId == Enum.PaymentMethod.TRANSFER) {
      filterOptions.push({
        title: 'payeeCategory',
        field: 'payeeCategory',
        opt: 7,
        value: "CUSTOMER"
      })
    } else if (filterParams?.paymentMethodId == Enum.PaymentMethod.THIRD_PART && filterParams?.payToUserId) {
      filterOptions.push({
        title: 'payeeCategory',
        field: 'payeeCategory',
        opt: 7,
        value: "EMPLOYEE"
      })
    } else if (filterParams?.paymentMethodId == Enum.PaymentMethod.THIRD_PART && filterParams?.supplierId) {
      filterOptions.push({
        title: 'payeeCategory',
        field: 'payeeCategory',
        opt: 7,
        value: "VENDER"
      })
    }

    //支付方式是第三方&给供应商时则为第1条明细.supplier的编号，第三方&给员工时为sp的pay_to_user_id的工号，转账为第1条明细.soldto.sold_to_real_code
    if (filterParams?.paymentMethodId == Enum.PaymentMethod.TRANSFER) {
      //转账为第1条明细 如果没值取表头的soldToCode
      if (filterParams?.formSpDetail && filterParams?.formSpDetail?.soldToCode) {
        const soldTo = await SoldTo.get(filterParams?.formSpDetail?.soldToCode)
        filterOptions.push({
          title: 'payeeId',
          field: 'payeeId',
          opt: 7,
          value: soldTo?.data?.soldToRealCode
        })
      } else {
        if (filterParams?.soldToCode) {
          const soldTo = await SoldTo.get(filterParams?.soldToCode)
          filterOptions.push({
            title: 'payeeId',
            field: 'payeeId',
            opt: 7,
            value: soldTo?.data?.soldToRealCode
          })
        }
      }
    } else if (filterParams?.paymentMethodId == Enum.PaymentMethod.THIRD_PART && filterParams?.payToUserId) {
      const staff = await Staff.get(filterParams?.payToUserId)
      filterOptions.push({
        title: 'payeeId',
        field: 'payeeId',
        opt: 7,
        value: staff?.data?.staffNo
      })
    } else if (filterParams?.paymentMethodId == Enum.PaymentMethod.THIRD_PART && filterParams?.supplierId) {
      const supplier = await Supplier.get(filterParams?.supplierId)
      filterOptions.push({
        title: 'payeeId',
        field: 'payeeId',
        opt: 7,
        value: supplier?.data?.supplierCode
      })

    }
    return filterOptions;
  })

  const fetchData = (async () => {
    setLoading(true);
    const filterOptions = await getFilterOptions();
    const req: any = {
      filterOptions: JSON.stringify(filterOptions),
      id: 'SpReversePreSelect',
      pagenum: 0,
      pagesize: 10,
      datafield: 'submitDate',
      sortorder: 'DESC'
    };
    ReversePrepayDetail.queryReversePrepay(req).then(responseResult => {
      if (responseResult.code != ResponseStatus.SUCCESS) {
        message.error(responseResult.message)
        setLoading(false);
        return;
      }
      const data = responseResult.data;
      if (data) {
        setDataSource(data.records);
        setLoading(false);
      }
    });
  });


  const onSelectChange = (record: API.ReversePrepayDetailVo, selected: boolean) => {
    if (!record.requisitionNumber || !record.transactionNum) return;
    if (selected) {
      addSelectedRowState(record)
      return;
    }
    delSelectedRowState(record)
  };

  const onSelectAllChange = (selected: boolean, selectedRows: API.ReversePrepayDetailVo[], changeRows: API.ReversePrepayDetailVo[]) => {
    if (selected) {
      changeRows.forEach(item => addSelectedRowState(item))
      return;
    }
    changeRows.forEach(item => delSelectedRowState(item))
  };

  const doSave = () => {
    const rows = Array.from(selectedRowsState.rows.values());
    if (!rows || rows.length == 0) {
      message.error('选择要冲销的预付单');
      return false;
    }
    props.onSave?.({code: ResponseStatus.SUCCESS, data: rows});
    return true;
  };

  return (
    <WdiModal
      title={props.title}
      visible={props.visible}
      width={1200}
      bodyStyle={{height: 600}}
      onCancel={() => {
        props.onClose?.()
      }}
      onOk={doSave}
    >
      <WdiTable
        rowSelection={{
          selectedRowKeys: Array.from(selectedRowsState.rows.keys()),
          onSelect: onSelectChange,
          onSelectAll: onSelectAllChange
        }}
        columns={tableColumns}
        rowKey={buildRowKey}
        showPageRows={10}
        dataSource={dataSource}
        pagination={false}
        loading={loading}
      />
    </WdiModal>
  );
};

